import * as React from 'react';
import { Menu, MenuItem, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import ListIcon from '@mui/icons-material/List';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { pushToast, useDispatch } from '../../state';
import {
  openDeleteInvoiceModal,
  openEditInvoiceModal,
  InvoiceModalMode,
} from '../../state/invoiceModalSlice';
import type { Invoice } from '@repo/api-contract';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileSignature } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { openAuditHistoryModalThunk } from '../../state/auditHistoryModalSlice';
import client from '../../util/api-client';
import { TRPCClientError } from '@trpc/client';
import { serverError } from '../modals/constants/errors';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

interface BasicMenuProps {
  invoice: Invoice;
}

export default function BasicMenu(props: BasicMenuProps) {
  // const {searchInput, searchBy} = useSelector((store) => store.invoiceBrowseSlice)
  const { t: tCI } = useTranslation([], {
    keyPrefix: 'team.Components.InvoiceBrowseTable',
  });
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditInvoice = () => {
    dispatch(
      openEditInvoiceModal({
        invoiceIdToEdit: props.invoice.InvoiceId,
        updateCallback: async (invoice) => {
          if (invoice) {
            // Here you can handle updates with props.invoice and user
          }
        },
      }),
    );
    setAnchorEl(null);
  };

  const handleViewInvoice = () => {
    dispatch(
      openEditInvoiceModal({
        invoiceIdToEdit: props.invoice.InvoiceId,
        mode: InvoiceModalMode.VIEW,
        updateCallback: async (invoice) => {
          if (invoice) {
            // Here you can handle updates with props.invoice and user
          }
        },
      }),
    );
    setAnchorEl(null);
  };

  const handleViewTimeline = () => {
    setAnchorEl(null);
    dispatch(
      openAuditHistoryModalThunk({
        resource: 'invoice',
        resourceId: props.invoice.InvoiceId,
        backToLabel: tCI('Browse Invoices'),
      }),
    );
  };

  const handleDeleteInvoice = () => {
    setAnchorEl(null);
    dispatch(
      openDeleteInvoiceModal({
        invoiceToDelete: props.invoice,
        // it takes too long for changes to propogate after deleting a invoice

        // deleteCallback: async () => {
        //   const payload:InvoiceSearchInput = { query: searchInput, searchBy, size: 100, page: 0}
        //   dispatch(searchInvoices(payload))
        // }
      }),
    );
  };

  const handleOpenPDF = async () => {
    setAnchorEl(null);
    document.body.style.cursor = 'wait';

    try {
      const pdfBase64 = await client.getInvoicePDF.query(
        props.invoice.InvoiceId,
      );

      // Convert the base64 string to a binary array
      const binary = atob(pdfBase64);
      const array = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        array[i] = binary.charCodeAt(i);
      }

      // Create a Blob from the binary array
      const blob = new Blob([array], { type: 'application/pdf' });

      // Create an object URL for the Blob
      const url = URL.createObjectURL(blob);

      // Open the PDF in a new tab
      window.open(url, '_blank');

      // Optionally, revoke the object URL after some time to free up memory
      // setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.error('error', error);
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
    } finally {
      document.body.style.cursor = 'default';
    }
  };

  const OptionsMUIIconSize = 30;
  const OptionsFAIconSize = 21;

  return (
    <div>
      <IconButton
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        className="text-2xl tracking-tight rounded-full binvoice-0"
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem
          onClick={handleEditInvoice}
          style={{ color: 'rgb(51, 99, 255)' }}
        >
          <div className="w-8 h-[30px] flex items-center justify-center">
            <EditIcon style={{ fontSize: OptionsMUIIconSize }} />
          </div>
          &nbsp; Edit Invoice
        </MenuItem>
        <MenuItem onClick={handleClose} style={{ color: 'rgb(51, 99, 255)' }}>
          <div className="w-8 h-[30px] flex items-center justify-center">
            <ListIcon style={{ fontSize: OptionsMUIIconSize }} />
          </div>
          &nbsp; View Invoice History
        </MenuItem>
        <MenuItem
          onClick={handleViewInvoice}
          style={{ color: 'rgb(51, 99, 255)' }}
        >
          <div className="w-8 h-[30px] flex items-center justify-center">
            <FontAwesomeIcon
              icon={faFileSignature}
              style={{ fontSize: OptionsFAIconSize }}
            />
          </div>
          &nbsp; View Invoice
        </MenuItem>
        <MenuItem onClick={handleOpenPDF} style={{ color: 'rgb(51, 99, 255)' }}>
          <div className="w-8 h-[30px] flex items-center justify-center">
            <PictureAsPdfIcon style={{ fontSize: OptionsMUIIconSize }} />
          </div>
          &nbsp; View PDF
        </MenuItem>
        <MenuItem
          onClick={handleViewTimeline}
          style={{ color: 'rgb(51, 99, 255)' }}
        >
          <div className="w-8 h-[30px] flex items-center justify-center">
            <HistoryIcon style={{ fontSize: OptionsMUIIconSize }} />
          </div>
          &nbsp; View Timeline
        </MenuItem>
        <MenuItem
          onClick={handleDeleteInvoice}
          style={{ color: 'rgb(51, 99, 255)' }}
        >
          <div className="w-8 h-[30px] flex items-center justify-center">
            <DeleteIcon style={{ fontSize: OptionsMUIIconSize }} />
          </div>
          &nbsp; Delete
        </MenuItem>
      </Menu>
    </div>
  );
}
